import React from "react";
import { Inbox } from "lucide-react";
import { cn } from "../../lib/utils";

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  className?: string;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: string;
  testId?: string;
  rowTestId?: (row: T) => string;
  rowClassName?: (row: T) => string;
}

export function DataTable<T>({ columns, rows, rowKey, onRowClick, loading, empty = "No records found", testId, rowTestId, rowClassName }: Props<T>) {
  return (
    <div className="card-crisp overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={cn("th", c.className)}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="td py-10 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center">
                  <Inbox className="mx-auto h-6 w-6 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-500">{empty}</p>
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr
                  key={rowKey(r)}
                  data-testid={rowTestId ? rowTestId(r) : undefined}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  className={cn("row-hover transition-opacity", onRowClick && "cursor-pointer", rowClassName?.(r))}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={cn("td", c.className)}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
